import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { useQuery } from '@apollo/client';

import type { SgaAiAssistedKeyVersionEpicValue } from 'bundles/grading-common/types/EpicTypes';
import { getSgaOpenAIProdKeyVersion, getSgaOpenAITestKeyVersion } from 'bundles/grading-common/utils/epicUtils';
import getAiSuggestionsQuery from 'bundles/grading/api/GetAiSuggestionsQuery.graphql';
import type {
  GetAiSuggestionsQuery,
  GetAiSuggestionsQueryVariables,
} from 'bundles/grading/api/__generated__/GetAiSuggestionsQuery';
import type { AiReviewPartSuggestion } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import {
  addAiSuggestionInteractionToLog,
  getAiSuggestionInteractionLogFromLocalStorage,
} from 'bundles/grading/utils/aiSuggestionsUtils';
import type { AiSuggestionRating } from 'bundles/grading/utils/aiSuggestionsUtils';
import { useEventTracker } from 'bundles/grading/utils/hooks';

type Props = {
  children: ReactNode;
};

const AiSuggestionsProvider = ({ children }: Props) => {
  const { pageData, submissionData, isPrivateSession } = useContext(SGAPageContext);
  const [aiReviewPartSuggestions, setAiReviewPartSuggestions] = useState<AiReviewPartSuggestion[]>([]);
  const [currentAiReviewPartSuggestion, setCurrentAiReviewPartSuggestion] = useState<AiReviewPartSuggestion | null>(
    null
  );
  const trackAiSuggestionAction = useEventTracker();

  const { submission, submitter } = submissionData.data || {};

  // start the state as applied so that there's no chance for the ai suggestion to be applicable while waiting to find the real state
  const [currentAiReviewPartSuggestionApplied, setCurrentAiReviewPartSuggestionApplied] = useState(true);
  const [currentAiReviewPartSuggestionEdited, setCurrentAiReviewPartSuggestionEdited] = useState(false);
  const [currentAiReviewPartSuggestionRating, setCurrentAiReviewPartSuggestionRating] =
    useState<AiSuggestionRating>(null);

  // update the current review part's ai suggestion if it exists
  useEffect(() => {
    setCurrentAiReviewPartSuggestion(
      aiReviewPartSuggestions?.find((suggestion) => {
        const suggestionId = suggestion.id.split('~')[1];
        return submission?.currentRubricId && suggestionId === submission.currentRubricId;
      }) || null
    );
  }, [aiReviewPartSuggestions, submission?.currentRubricId]);

  // TODO: once there is a BE implementation we can remove this
  // using separate effect so that the changes in currentAiReviewPartSuggestion doesn't cause the previous useEffect to run twice
  // when adding currentAiReviewPartSuggestion to dependency array
  useEffect(() => {
    const interactionLog = getAiSuggestionInteractionLogFromLocalStorage() || {};
    setCurrentAiReviewPartSuggestionApplied(
      !!currentAiReviewPartSuggestion &&
        !!interactionLog?.[currentAiReviewPartSuggestion.id] &&
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        interactionLog[currentAiReviewPartSuggestion.id].applied
    );
    setCurrentAiReviewPartSuggestionEdited(
      !!currentAiReviewPartSuggestion &&
        !!interactionLog?.[currentAiReviewPartSuggestion.id] &&
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        interactionLog[currentAiReviewPartSuggestion.id].edited
    );
    setCurrentAiReviewPartSuggestionRating(
      !!currentAiReviewPartSuggestion && !!interactionLog?.[currentAiReviewPartSuggestion.id]
        ? // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          interactionLog[currentAiReviewPartSuggestion.id].rating
        : null
    );
  }, [currentAiReviewPartSuggestion]);

  const trackSuggestionApplied = () => {
    if (currentAiReviewPartSuggestion) {
      addAiSuggestionInteractionToLog(
        currentAiReviewPartSuggestion.id,
        true,
        currentAiReviewPartSuggestionEdited,
        currentAiReviewPartSuggestionRating
      );
      trackAiSuggestionAction({
        trackingName: `quick_grader_sga_ai_suggestion_applied`,
        action: 'click',
        trackingData: {
          suggestionId: currentAiReviewPartSuggestion.id,
        },
      });
      setCurrentAiReviewPartSuggestionApplied(true);
    }
  };

  const trackSuggestionEdited = () => {
    if (currentAiReviewPartSuggestion) {
      addAiSuggestionInteractionToLog(
        currentAiReviewPartSuggestion.id,
        currentAiReviewPartSuggestionApplied,
        true,
        currentAiReviewPartSuggestionRating
      );
      trackAiSuggestionAction({
        trackingName: `quick_grader_sga_ai_suggestion_edited`,
        action: 'edit',
        trackingData: {
          suggestionId: currentAiReviewPartSuggestion.id,
        },
      });
      setCurrentAiReviewPartSuggestionEdited(true);
    }
  };

  const trackSuggestionRating = (rating: AiSuggestionRating) => {
    if (currentAiReviewPartSuggestion) {
      addAiSuggestionInteractionToLog(
        currentAiReviewPartSuggestion.id,
        currentAiReviewPartSuggestionApplied,
        currentAiReviewPartSuggestionEdited,
        rating
      );
      trackAiSuggestionAction({
        trackingName: `quick_grader_sga_ai_suggestion_feedback`,
        action: 'click',
        trackingData: {
          suggestionId: currentAiReviewPartSuggestion.id,
          // using same convention as peer ai grading where null re[resents unsetting of feedback
          feedback: rating === null ? 'UNSET' : rating,
        },
      });
      setCurrentAiReviewPartSuggestionRating(rating);
    }
  };

  const epicValuesOpenAiProd: SgaAiAssistedKeyVersionEpicValue = getSgaOpenAIProdKeyVersion();
  const epicValuesOpenAiTest: SgaAiAssistedKeyVersionEpicValue = getSgaOpenAITestKeyVersion();
  const isEnabled =
    epicValuesOpenAiTest.courseIds.includes(pageData?.courseId) ||
    epicValuesOpenAiProd.courseIds.includes(pageData?.courseId);

  const processFetchData = (data: GetAiSuggestionsQuery) => {
    setAiReviewPartSuggestions(data?.AiSuggestions?.getAiSuggestions || []);
  };

  const { loading } = useQuery<GetAiSuggestionsQuery, GetAiSuggestionsQueryVariables>(getAiSuggestionsQuery, {
    variables: {
      courseId: pageData.courseId,
      groupId: pageData.currentContextId,
      itemId: pageData.currentItemId,
      submitterId: submitter?.submitterId || '',
    },
    onCompleted: processFetchData,
    context: { clientName: 'gatewayGql' },
    skip: !isEnabled || !submitter?.submitterId || !submission || !isPrivateSession,
  });

  return (
    <AiSuggestionsContext.Provider
      value={{
        loading,
        aiReviewPartSuggestions,
        currentAiReviewPartSuggestion,

        // TODO: temporary items fetched from local storage, eventually we will have these states persist in BE
        currentAiReviewPartSuggestionApplied,
        trackSuggestionApplied,
        currentAiReviewPartSuggestionEdited,
        trackSuggestionEdited,
        currentAiReviewPartSuggestionRating,
        trackSuggestionRating,
      }}
    >
      {children}
    </AiSuggestionsContext.Provider>
  );
};

export default AiSuggestionsProvider;
